<template>
  <div class="customer-pets flex flex-col items-center">
    <div class="lg:!w-7/12 flex justify-between items-center mb-4">
      <h2 class="p-0 text-lg font-bold">{{ t('pets') }}</h2>
      <el-button
        type="primary"
        @click="addPet"
        class="add-pet-button"
      >
        {{ t('addPet') }}
      </el-button>
    </div>

    <div v-if="localPets.length > 0" class="pets-list lg:!w-9/12">
      <!-- Header Row -->
      <div class="pets-header flex gap-2 mb-2 p-2 rounded-t-lg">
        <div style="width: 5%" class="font-semibold">#</div>
        <div style="width: 15%" class="font-semibold">
          <span class="text-red-500">* </span>{{ t('petName') }}
        </div>
        <div style="width: 15%" class="font-semibold">{{ t('dateOfBirth') }}</div>
        <div style="width: 15%" class="font-semibold">{{ t('type') }}</div>
        <div style="width: 15%" class="font-semibold">{{ t('sex') }}</div>
        <div style="width: 20%" class="font-semibold">{{ t('breed') }}</div>
        <div style="width: 15%" class="font-semibold">{{ t('color') }}</div>
        <div style="width: 10%" class="font-semibold">{{ t('neutered') }}</div>
        <div style="width: 10%"></div>
      </div>

      <!-- Pet Rows -->
      <div
        v-for="(pet, index) in localPets"
        :key="pet.id || index"
        class="pet-row flex gap-2 mb-2 p-2 rounded-lg transition-all"
        :class="{ 'deleted-pet': pet.isDeleted }"
      >
        <!-- Index -->
        <div style="width: 5%" class="flex items-center">
          {{ index + 1 }}
        </div>

        <!-- Pet Name -->
        <div style="width: 15%">
          <el-input
            v-model="pet.name"
            :placeholder="t('petName')"
            :disabled="pet.isDeleted"
            :class="{ 'pet-name-invalid': isNameInvalid(pet) }"
            @input="emitUpdate"
            maxlength="127"
            clearable
          />
          <div v-if="isNameInvalid(pet)" class="text-red-500 text-xs mt-1">
            {{ t('petNameRequired') }}
          </div>
        </div>

        <!-- Date of Birth -->
        <div style="width: 15%">
          <el-date-picker
            v-model="pet.dateOfBirth"
            type="date"
            format="DD-MM-YYYY"
            :placeholder="t('dateOfBirth')"
            :disabled="pet.isDeleted"
            @change="emitUpdate"
            style="width: 100%"
          />
        </div>

        <!-- Type -->
        <div style="width: 15%">
          <el-select
            v-model="pet.type"
            :placeholder="t('type')"
            :disabled="pet.isDeleted"
            @change="emitUpdate"
            clearable
            filterable
            class="w-full"
          >
            <el-option
              v-for="petType in petTypes"
              :key="petType"
              :label="petType"
              :value="petType"
            />
          </el-select>
        </div>

        <!-- Sex -->
        <div style="width: 15%">
          <el-select
            v-model="pet.sex"
            :placeholder="t('sex')"
            :disabled="pet.isDeleted"
            @change="emitUpdate"
            clearable
            class="w-full"
          >
            <el-option :label="t('female')" value="female" />
            <el-option :label="t('male')" value="male" />
          </el-select>
        </div>

        <!-- Breed -->
        <div style="width: 20%">
          <deletable-autocomplete
            v-if="!pet.isDeleted"
            :id="`breed-${index}`"
            v-model="pet.breed"
            :placeholder="t('breed')"
            :fetch-suggestions="breedSearch"
            @update:modelValue="emitUpdate"
            :maxlength="127"
          />
          <el-input
            v-else
            v-model="pet.breed"
            :placeholder="t('breed')"
            disabled
          />
        </div>

        <!-- Color -->
        <div style="width: 15%">
          <deletable-autocomplete
            v-if="!pet.isDeleted"
            :id="`color-${index}`"
            v-model="pet.color"
            :placeholder="t('color')"
            :fetch-suggestions="colorSearch"
            @update:modelValue="emitUpdate"
            :maxlength="127"
          />
          <el-input
            v-else
            v-model="pet.color"
            :placeholder="t('color')"
            disabled
          />
        </div>

        <!-- Neutered -->
        <div style="width: 10%" class="flex items-center justify-center">
          <el-checkbox
            v-model="pet.neutered"
            :disabled="pet.isDeleted"
            @change="emitUpdate"
          />
        </div>

        <!-- Actions -->
        <div style="width: 10%" class="flex items-center justify-center gap-2">
          <!-- Delete/Remove Button -->
          <el-button
            v-if="!pet.isDeleted"
            type="danger"
            :icon="Delete"
            circle
            size="small"
            @click="toggleDelete(index)"
          />
          <!-- Restore Button -->
          <el-button
            v-else
            type="success"
            :icon="RefreshLeft"
            circle
            size="small"
            @click="toggleDelete(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { Delete, RefreshLeft } from '@element-plus/icons-vue'
import DeletableAutocomplete from '@/components/form/DeletableAutocomplete.vue'
import CustomerPet, { customerPetI18nMessages } from '@/models/customer/CustomerPet'
import { useSettingsStore } from '@/stores/settingsStore'

interface Suggestion {
  id?: number
  value: string
}

export default defineComponent({
  name: 'CustomerPets',

  components: {
    DeletableAutocomplete
  },

  props: {
    pets: {
      type: Array as PropType<CustomerPet[]>,
      required: true,
      default: () => []
    },
    customerId: {
      type: String,
      default: ''
    }
  },

  emits: ['update:pets'],

  setup() {
    const { t } = useI18n({
      useScope: 'global',
      messages: customerPetI18nMessages
    })

    return { t, Delete, RefreshLeft }
  },

  data() {
    return {
      localPets: [] as CustomerPet[],
      petTypes: [] as string[],
      // becomes true after a failed save attempt, so errors only show once the
      // user has tried to submit
      showErrors: false
    }
  },

  watch: {
    pets: {
      handler(newVal: CustomerPet[]) {
        this.localPets = newVal.map((pet: CustomerPet) => CustomerPet.from(pet))
      },
      immediate: true,
      deep: true
    }
  },

  mounted() {
    // Load pet types from settings
    const settingsStore = useSettingsStore()
    const petTypesValue = settingsStore.getValue('pet_types')

    if (petTypesValue) {
      try {
        this.petTypes = typeof petTypesValue === 'string'
          ? JSON.parse(petTypesValue)
          : petTypesValue
      } catch (e) {
        console.error('Error parsing pet_types:', e)
        this.petTypes = []
      }
    }
  },

  methods: {
    addPet() {
      const newPet = new CustomerPet()
      newPet.customerId = this.customerId
      newPet.isDeleted = false
      this.localPets.push(newPet)
      this.emitUpdate()
    },

    toggleDelete(index: number) {
      const pet = this.localPets[index]

      // If the customer is saved (pet has an id), just toggle isDeleted flag
      // Otherwise, remove it from the array completely
      if (pet.id) {
        pet.isDeleted = !pet.isDeleted
      } else {
        // Remove new unsaved pets completely
        this.localPets.splice(index, 1)
      }

      this.emitUpdate()
    },

    emitUpdate() {
      this.$emit('update:pets', this.localPets)
    },

    // A (non-deleted) pet must have a name.
    isNameInvalid(pet: CustomerPet): boolean {
      return this.showErrors && !pet.isDeleted && !(pet.name && pet.name.trim())
    },

    // Called by the parent form before saving. Returns true when every pet has a
    // name; otherwise flags the empty rows (red border + message) and returns false.
    validate(): boolean {
      this.showErrors = true
      return this.localPets.every((pet) => pet.isDeleted || !!(pet.name && pet.name.trim()))
    },

    async breedSearch(queryString: string, cb: any) {
      try {
        const response = await this.$http.get<Suggestion[]>(
          `/helpers/suggestions?model=customer_pets&key=breed&query=${queryString}`
        )
        const result = response.map((r: Suggestion) => ({
          value: r.value,
          id: r.id
        }))
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },

    async colorSearch(queryString: string, cb: any) {
      try {
        const response = await this.$http.get<Suggestion[]>(
          `/helpers/suggestions?model=customer_pets&key=color&query=${queryString}`
        )
        const result = response.map((r: Suggestion) => ({
          value: r.value,
          id: r.id
        }))
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    }
  }
})
</script>

<style scoped>
.customer-pets {
  @apply w-full;
}

.add-pet-button {
  @apply bg-purple-400 hover:bg-purple-500 border-purple-500;
}

.pets-header {
  @apply bg-purple-200 text-purple-900;
}

.pet-row {
  @apply bg-purple-50 border border-purple-200;
}

.deleted-pet {
  @apply bg-gray-200 opacity-60;
}

.deleted-pet input,
.deleted-pet .el-input__inner,
.deleted-pet .el-select {
  @apply bg-gray-100;
}

.pet-name-invalid :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px var(--el-color-danger) inset;
}

</style>
